import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatCurrency } from "helpers";
import { FleetVehicleExpenseItem, FleetVehicleSellingPricePoint } from "services/api/fleet/vehicles/expenses/types";

export interface ExpenseChartBucket {
    label: string;
    items: FleetVehicleExpenseItem[];
    cumulative: number;
}

interface VehicleExpenseChartProps {
    buckets: ExpenseChartBucket[];
    windowStart: Date;
    windowEndDate: Date;
    priceHistory: FleetVehicleSellingPricePoint[];
    bucketTooltipTitle?: (bucket: ExpenseChartBucket) => string;
    // Opcionalno: klik na mjesečni bucket (drill-down na pretragu troškova). Ako nije zadano, bucket nije klikabilan.
    onBucketClick?: (bucketIndex: number) => void;
}

const VehicleExpenseChart: React.FC<VehicleExpenseChartProps> = ({
    buckets,
    windowStart,
    windowEndDate,
    priceHistory,
    bucketTooltipTitle,
    onBucketClick,
}) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const [hoverPriceIndex, setHoverPriceIndex] = useState<number | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({ w: 800, h: 400 });
    const uid = useId().replace(/[:]/g, "");
    const expGradId = `vehExpGrad-${uid}`;
    const priceGradId = `vehPriceGrad-${uid}`;

    useLayoutEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const update = () => {
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) setSize({ w: rect.width, h: rect.height });
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const cumulatives = useMemo(() => buckets.map(b => b.cumulative ?? 0), [buckets]);
    const currentPrice = priceHistory.length > 0 ? priceHistory[priceHistory.length - 1].price : 0;

    const rawMax = Math.max(...cumulatives, ...priceHistory.map(p => p.price), 0);
    const dataMax = Math.max(rawMax, 100);

    const ticks = useMemo(() => {
        const target = 4;
        const raw = dataMax / target;
        const mag = Math.pow(10, Math.floor(Math.log10(raw)));
        const norm = raw / mag;
        let niceStep;
        if (norm < 1.5) niceStep = 1;
        else if (norm < 3) niceStep = 2;
        else if (norm < 7) niceStep = 5;
        else niceStep = 10;
        const step = niceStep * mag;
        const niceMax = Math.ceil(dataMax / step) * step;
        const arr: number[] = [];
        for (let v = 0; v <= niceMax + 1e-9; v += step) {
            const rounded = Math.round(v);
            if (arr.length === 0 || arr[arr.length - 1] !== rounded) arr.push(rounded);
        }
        return arr;
    }, [dataMax]);

    const yMax = ticks[ticks.length - 1] || 1;

    const chartW = size.w;
    const chartH = size.h;
    const padLeft = 88;
    const padRight = 24;
    const padY = 32;

    const toPoint = (val: number, i: number) => {
        const count = Math.max(cumulatives.length, 2);
        const x = padLeft + (i / (count - 1)) * (chartW - padLeft - padRight);
        const y = padY + (1 - val / yMax) * (chartH - 2 * padY);
        return { x, y };
    };

    const dateToX = (d: Date | string): number => {
        const date = typeof d === "string" ? new Date(d) : d;
        const totalMs = windowEndDate.getTime() - windowStart.getTime();
        if (totalMs <= 0) return padLeft;
        const offset = date.getTime() - windowStart.getTime();
        const ratio = Math.max(0, Math.min(1, offset / totalMs));
        return padLeft + ratio * (chartW - padLeft - padRight);
    };

    const priceToY = (price: number): number => padY + (1 - price / yMax) * (chartH - 2 * padY);

    const buildSmoothPath = (data: number[]) => {
        const points = data.map((v, i) => toPoint(v, i));
        if (points.length < 2) return "";
        let d = `M ${points[0].x} ${points[0].y}`;
        for (let i = 0; i < points.length - 1; i++) {
            const p0 = points[i];
            const p1 = points[i + 1];
            const t = 0.2;
            const cp1x = p0.x + (p1.x - p0.x) * t;
            const cp2x = p1.x - (p1.x - p0.x) * t;
            d += ` C ${cp1x} ${p0.y}, ${cp2x} ${p1.y}, ${p1.x} ${p1.y}`;
        }
        return d;
    };

    const buildAreaPath = (data: number[]) => {
        if (data.length < 2) return "";
        const line = buildSmoothPath(data);
        const lastPoint = toPoint(data[data.length - 1], data.length - 1);
        const firstPoint = toPoint(data[0], 0);
        return `${line} L ${lastPoint.x} ${chartH} L ${firstPoint.x} ${chartH} Z`;
    };

    const buildStepPath = (pts: { x: number; y: number }[]): string => {
        if (pts.length === 0) return "";
        let d = `M ${pts[0].x} ${pts[0].y}`;
        for (let i = 1; i < pts.length; i++) {
            d += ` L ${pts[i].x} ${pts[i - 1].y} L ${pts[i].x} ${pts[i].y}`;
        }
        const endX = chartW - padRight;
        const last = pts[pts.length - 1];
        if (endX > last.x) d += ` L ${endX} ${last.y}`;
        return d;
    };

    const buildStepAreaPath = (pts: { x: number; y: number }[]): string => {
        if (pts.length === 0) return "";
        const line = buildStepPath(pts);
        const endX = chartW - padRight;
        return `${line} L ${endX} ${chartH} L ${pts[0].x} ${chartH} Z`;
    };

    const pricePoints = useMemo(() => priceHistory.map(p => ({
        x: dateToX(p.date),
        y: priceToY(p.price),
        price: p.price,
        date: p.date,
    })), [priceHistory, chartW, chartH, yMax, windowStart, windowEndDate]);

    const formatDate = (d: Date | string) => {
        const date = typeof d === "string" ? new Date(d) : d;
        if (!date || isNaN(date.getTime())) return "";
        return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.`;
    };

    return (
        <div className="w-full mb-2 flex-1 flex flex-col min-h-0">
            <div
                ref={wrapperRef}
                className="relative flex-1 min-h-0"
                onMouseLeave={() => { setHoverIndex(null); setHoverPriceIndex(null); }}
            >
                <svg
                    viewBox={`0 0 ${chartW} ${chartH}`}
                    width={chartW}
                    height={chartH}
                    className="block absolute inset-0 w-full h-full animate-chartWipe"
                >
                    <defs>
                        <linearGradient id={expGradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.25" />
                            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.02" />
                        </linearGradient>
                        <linearGradient id={priceGradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10b981" stopOpacity="0.20" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
                        </linearGradient>
                    </defs>

                    {ticks.map((tick, idx) => {
                        const y = padY + (1 - tick / yMax) * (chartH - 2 * padY);
                        return (
                            <g key={`tick-${idx}`}>
                                <line x1={padLeft} y1={y} x2={chartW - padRight} y2={y} stroke="#e2e8f0" strokeWidth="1" />
                                <text x={padLeft - 12} y={y} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="#94a3b8">
                                    {formatCurrency.format(tick)}
                                </text>
                            </g>
                        );
                    })}

                    <path d={buildAreaPath(cumulatives)} fill={`url(#${expGradId})`} />
                    <path d={buildSmoothPath(cumulatives)} fill="none" stroke="#f43f5e" strokeWidth="2" strokeDasharray="6 4" strokeLinecap="round" />

                    {pricePoints.length > 0 && (
                        <>
                            <path d={buildStepAreaPath(pricePoints)} fill={`url(#${priceGradId})`} />
                            <path d={buildStepPath(pricePoints)} fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" strokeLinecap="round" strokeLinejoin="round" />
                            {pricePoints.map((p, idx) => (
                                <circle
                                    key={idx}
                                    cx={p.x}
                                    cy={p.y}
                                    r={hoverPriceIndex === idx ? 6 : 3.5}
                                    fill="#10b981"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    style={{ transition: "r 120ms", pointerEvents: "none" }}
                                />
                            ))}
                        </>
                    )}

                    {buckets.map((_, i) => {
                        const expPt = toPoint(cumulatives[i], i);
                        const innerW = chartW - padLeft - padRight;
                        const zoneW = innerW / Math.max(buckets.length, 1);
                        const zoneX = padLeft + i * zoneW;
                        return (
                            <g
                                key={i}
                                onMouseEnter={() => setHoverIndex(i)}
                                onClick={onBucketClick ? () => onBucketClick(i) : undefined}
                            >
                                <rect
                                    x={zoneX}
                                    y={0}
                                    width={zoneW}
                                    height={chartH}
                                    fill="transparent"
                                    style={onBucketClick ? { cursor: "pointer" } : undefined}
                                />
                                {hoverIndex === i && (
                                    <>
                                        <line x1={expPt.x} y1={padY} x2={expPt.x} y2={chartH - padY} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
                                        <circle cx={expPt.x} cy={expPt.y} r="5" fill="#f43f5e" stroke="white" strokeWidth="2" />
                                    </>
                                )}
                            </g>
                        );
                    })}

                    {/* Invisible thick stroke za hover detekciju cijele zelene linije — renderira se ZADNJI da nadjača bucket rect mouse priority */}
                    {pricePoints.length > 0 && (
                        <path
                            d={buildStepPath(pricePoints)}
                            fill="none"
                            stroke="transparent"
                            strokeWidth="20"
                            style={{ cursor: "pointer", pointerEvents: "stroke" }}
                            onMouseMove={(e) => {
                                const svgEl = e.currentTarget.ownerSVGElement;
                                if (!svgEl) return;
                                const pt = svgEl.createSVGPoint();
                                pt.x = e.clientX;
                                pt.y = e.clientY;
                                const ctm = svgEl.getScreenCTM();
                                if (!ctm) return;
                                const local = pt.matrixTransform(ctm.inverse());
                                let activeIdx = 0;
                                for (let i = 0; i < pricePoints.length; i++) {
                                    if (pricePoints[i].x <= local.x) activeIdx = i;
                                    else break;
                                }
                                setHoverPriceIndex(activeIdx);
                                setHoverIndex(null);
                            }}
                            onMouseLeave={() => setHoverPriceIndex(null)}
                        />
                    )}
                </svg>

                {hoverIndex !== null && (() => {
                    const pt = toPoint(cumulatives[hoverIndex], hoverIndex);
                    const leftPct = (pt.x / chartW) * 100;
                    const topPct = (pt.y / chartH) * 100;
                    const hovered = buckets[hoverIndex];
                    const items = hovered?.items ?? [];
                    const alignRight = leftPct > 60;
                    // Flip tooltip dolje kada je anchor u gornjoj polovici chart-a — sprječava klipanje parentom.
                    const renderBelow = topPct < 50;
                    const tooltipTitle = bucketTooltipTitle ? bucketTooltipTitle(hovered) : hovered.label;
                    return (
                        <div
                            className="absolute bg-slate-800 text-white text-sm rounded shadow-lg min-w-[260px] max-w-[360px] z-20"
                            style={{
                                left: `${leftPct}%`,
                                top: renderBelow ? `calc(${topPct}% + 12px)` : `calc(${topPct}% - 8px)`,
                                transform: `translate(${alignRight ? "calc(-100% - 12px)" : "12px"}, ${renderBelow ? "0%" : "-100%"})`,
                            }}
                        >
                            <div className="px-3 py-2 border-b border-slate-700">
                                <div className="text-xs uppercase text-slate-400 font-semibold">{tooltipTitle}</div>
                                <div className="text-xs text-slate-400 mt-0.5">Kumulativno</div>
                                <div className="text-base font-bold">{formatCurrency.format(hovered?.cumulative ?? 0)}</div>
                            </div>
                            {items.length > 0 ? (
                                <div className="px-3 py-2 max-h-[160px] overflow-y-auto">
                                    <div className="text-xs uppercase text-slate-400 font-semibold mb-1">Stavke ({items.length})</div>
                                    {items.map((it, idx) => (
                                        <div key={idx} className="flex items-baseline justify-between gap-3 py-1 border-b border-slate-700/50 last:border-0">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs text-slate-300 truncate">{it.name}</div>
                                                <div className="text-[10px] text-slate-500">
                                                    {formatDate(it.date)}
                                                    {it.invoiceNumber && <span className="ml-1 text-sky-300">· {it.invoiceNumber}</span>}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="text-xs text-rose-300">+{formatCurrency.format(it.amount)}</div>
                                                <div className="text-[10px] text-slate-400">{formatCurrency.format(it.cumulative)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="px-3 py-2 text-xs text-slate-400">Nema stavki</div>
                            )}
                        </div>
                    );
                })()}

                {hoverPriceIndex !== null && (() => {
                    const p = pricePoints[hoverPriceIndex];
                    if (!p) return null;
                    const leftPct = (p.x / chartW) * 100;
                    const topPct = (p.y / chartH) * 100;
                    const alignRight = leftPct > 60;
                    const date = typeof p.date === "string" ? new Date(p.date) : p.date;
                    const dateStr = date && !isNaN(date.getTime())
                        ? `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}.`
                        : "";
                    return (
                        <div
                            className="absolute bg-slate-800 text-white text-sm rounded shadow-lg min-w-[300px] pointer-events-none"
                            style={{
                                left: `${leftPct}%`,
                                top: `calc(${topPct}% - 8px)`,
                                transform: `translate(${alignRight ? "calc(-100% - 12px)" : "12px"}, -100%)`,
                            }}
                        >
                            <div className="px-4 py-3">
                                <div className="text-xs uppercase text-emerald-400 font-semibold tracking-wide">Prodajna cijena</div>
                                {dateStr && <div className="text-xs text-slate-400 mt-1">od {dateStr}</div>}
                                <div className="text-xl font-bold mt-2">{formatCurrency.format(p.price)}</div>
                            </div>
                        </div>
                    );
                })()}
            </div>

            <div className="flex justify-between mt-1" style={{ paddingLeft: padLeft, paddingRight: padRight }}>
                {buckets.map((b, i) => (
                    <span key={`${b.label}-${i}`} className="text-sm text-slate-400">{b.label}</span>
                ))}
            </div>

            <div className="flex items-center justify-center gap-4 mt-3">
                <div className="flex items-center gap-1.5">
                    <span className="w-4 h-0.5 rounded" style={{ backgroundImage: "repeating-linear-gradient(90deg, #f43f5e 0, #f43f5e 4px, transparent 4px, transparent 7px)" }} />
                    <span className="text-sm text-slate-500">Rashodi</span>
                </div>
                {pricePoints.length > 0 && (
                    <div className="flex items-center gap-1.5">
                        <span className="w-4 h-0.5 rounded" style={{ backgroundImage: "repeating-linear-gradient(90deg, #10b981 0, #10b981 4px, transparent 4px, transparent 7px)" }} />
                        <span className="text-sm text-slate-500">Prodajna cijena</span>
                        <span className="text-sm font-semibold text-emerald-600">{formatCurrency.format(currentPrice)}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VehicleExpenseChart;
